import React, { useMemo, useState } from 'react';
import { Empty, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from 'react-admin';
import dayjs from 'dayjs';
import {
    ColumnHeader,
    FilterField,
    HeaderColumn,
    InfiniteFoot,
    useInfinitePages,
} from '@src/components/Common/DataList';
import { formatDateAndTime, formatDateAndTimeFromNow } from '@src/helpers/utils';

const { Text } = Typography;

/** how many entries arrive per fetch */
const PAGE_SIZE = 20;

const SKELETON_COUNT = 5;

/**
 * What kind of change an entry records. django-simple-history writes one of
 * three marks, and the glyph and its colour are the fastest way to read a feed:
 * you can tell a deletion from a change without reading either.
 */
const KINDS: Record<string, { icon: React.ReactNode; accent: string; word: string }> = {
    '+': { icon: <PlusOutlined />, accent: '#15a66a', word: 'Added' },
    '~': { icon: <EditOutlined />, accent: 'var(--tm-primary, #5796cc)', word: 'Changed' },
    '-': { icon: <DeleteOutlined />, accent: '#e0457b', word: 'Removed' },
};

const DEFAULT_KIND = { icon: <EditOutlined />, accent: 'var(--tm-muted, #8a93a6)', word: 'Changed' };

/**
 * The message with the things it is about set as code.
 *
 * An activity message is a sentence built around names — a username, a role, a
 * group — and picking those out is what makes a wall of them scannable. The
 * words to pick out come with the entry, so nothing has to be guessed at.
 */
const Message = ({ activity }: { activity: any }) => {
    const text: string = activity.message || '';

    const keywords = useMemo(() => {
        const words = Object.values(activity?.extra_data || {})
            .filter((value) => typeof value === 'string' && value)
            .map((value) => String(value));
        words.push('ADMIN', 'GENERAL', 'READ_ONLY', 'SYSTEM');
        if (activity.history_username) words.push(activity.history_username);
        // longest first, so "READ_ONLY" is not split by a shorter match inside it
        return Array.from(new Set(words)).sort((a, b) => b.length - a.length);
    }, [activity]);

    if (keywords.length === 0) return <>{text}</>;

    // the capture group keeps the separators, so the sentence survives the split
    const escaped = keywords.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'gi'));

    return (
        <>
            {parts.map((part, index) =>
                keywords.some((word) => part.toLowerCase() === word.toLowerCase()) ? (
                    <Text key={index} code>
                        {part}
                    </Text>
                ) : (
                    <React.Fragment key={index}>{part}</React.Fragment>
                ),
            )}
        </>
    );
};

/**
 * One entry, on one line: what kind of change it was, what happened, and when.
 *
 * The message already names who did it and what to — it is written as a
 * sentence — so nothing is repeated beside it. It is cut to the line rather
 * than wrapped, with the whole of it in the tooltip.
 */
const ActivityEntry = ({ activity }: { activity: any }) => {
    const kind = KINDS[activity.history_type] || DEFAULT_KIND;

    return (
        <article className="tm-event" style={{ ['--tm-event-accent' as any]: kind.accent }}>
            <Tooltip title={kind.word}>
                <span className="tm-event__ico" aria-label={kind.word}>
                    {kind.icon}
                </span>
            </Tooltip>

            <Tooltip placement="topLeft" title={activity.message}>
                <span className="tm-event__message">
                    <Message activity={activity} />
                </span>
            </Tooltip>

            <Tooltip title={formatDateAndTime(activity.history_date)}>
                <span className="tm-event__time">{formatDateAndTimeFromNow(activity.history_date)}</span>
            </Tooltip>
        </article>
    );
};

/** Today, Yesterday, or the date — the heading a day's entries gather under. */
const dayLabel = (date: string) => {
    const day = dayjs(date).startOf('day');
    const today = dayjs().startOf('day');
    const diff = today.diff(day, 'day');
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return day.format(diff < 365 ? 'D MMMM' : 'D MMMM YYYY');
};

/**
 * What the team has been doing, newest first.
 *
 * A flat timeline of sentences is hard to place in time — "3 days ago" on every
 * line and nothing saying where one day ends. So the entries gather under the
 * day they happened, the day rides the top of the panel while its entries are
 * on screen, and the next page arrives by scrolling rather than by clicking
 * "Load more".
 */
const ActivityTab = ({
    teamId,
    /**
     * Read a team the caller is not a member of. The feed carries membership
     * history, so the endpoint refuses it to anyone who is neither a member nor
     * a platform admin asking for cross-team access — and this is how it is
     * asked for.
     */
    allTeams = false,
}: { teamId: number; allTeams?: boolean }) => {
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [page, setPage] = useState(1);

    const { data, loading } = useQuery({
        type: 'getAction',
        resource: 'teams',
        payload: {
            id: teamId,
            verb: 'activity',
            filter: {
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
                ...(allTeams ? { all_teams: true } : {}),
                ...filters,
            },
        },
    });

    const records: any[] = data?.results || [];
    const total: number = data?.count ?? 0;

    // A change is identified by which history table it is in and which row of
    // it — an entry's `id` is the object's, and one object changes many times.
    const { items, hasMore, sentinelRef, settling } = useInfinitePages({
        queryKey: JSON.stringify(Object.keys(filters).sort().map((key) => [key, filters[key]])),
        records,
        loading,
        total,
        page,
        setPage,
        getId: (record: any) => `${record.model}-${record.history_id}`,
    });

    /**
     * Only two of the filterset's fields reach the union behind this endpoint —
     * a username, matched exactly, and the two ends of a date range. `model` is
     * accepted by the filterset and then ignored, so it is not offered.
     */
    const fields: FilterField[] = useMemo(
        () => [
            {
                name: 'history_username',
                label: 'Member',
                type: 'select',
                resource: 'team_memberships',
                // that endpoint's filter options are team names, not usernames
                optionsFrom: 'list',
                mapOption: (record: any) => ({ value: record.username, label: record.username }),
                dataCy: 'activity-user-filter',
            },
            {
                name: 'history_date',
                label: 'When',
                type: 'dateRange',
                startName: 'history_date_after',
                endName: 'history_date_before',
                dataCy: 'activity-date-filter',
            },
        ],
        [],
    );

    const columns: HeaderColumn[] = useMemo(
        () => fields.map((field) => ({ label: field.label, fields: [field], dataCy: field.dataCy })),
        [fields],
    );

    /** the entries in order, cut into the days they happened on */
    const days = useMemo(() => {
        const groups: { label: string; entries: any[] }[] = [];
        items.forEach((entry) => {
            const label = dayLabel(entry.history_date);
            const last = groups[groups.length - 1];
            if (last && last.label === label) last.entries.push(entry);
            else groups.push({ label, entries: [entry] });
        });
        return groups;
    }, [items]);

    const renderBody = () => {
        if ((loading || settling) && items.length === 0) {
            return (
                <div className="tm-activity">
                    {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                        <div key={index} className="tm-skel" />
                    ))}
                </div>
            );
        }

        if (items.length === 0) {
            return <Empty className="tm-empty" description="Nothing has happened on this team yet" />;
        }

        return days.map((day) => (
            <section className="tm-day" key={day.label}>
                <span className="tm-day__label">{day.label}</span>
                {day.entries.map((entry) => (
                    <ActivityEntry key={`${entry.model}-${entry.history_id}`} activity={entry} />
                ))}
            </section>
        ));
    };

    return (
        <div className="tm-tab">
            <div className="tm-activity">
                <div className="tm-activity__filters">
                    <ColumnHeader
                        flow
                        columns={columns}
                        schema={fields}
                        values={filters}
                        onChange={setFilters}
                        dataCy="activity-filters"
                    />
                </div>

                {renderBody()}

                <InfiniteFoot
                    sentinelRef={sentinelRef}
                    loading={loading}
                    loaded={items.length}
                    total={total}
                    hasMore={hasMore}
                    noun="entry"
                    plural="entries"
                />
            </div>
        </div>
    );
};

export default ActivityTab;
